import React, { useState, useEffect, useMemo, useRef, useCallback, Suspense, lazy } from 'react';
import { Box, Grid, Alert, Skeleton, Typography } from '@mui/material';
import { useHistory, useParams, useRouteMatch, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import PageHeader    from '../../components/PageHeader';
import InvButton     from '../../components/InvButton';
import ItemFormTabs  from '../../components/item/ItemFormTabs';
import IdentityTab   from '../../components/item/IdentityTab';
import TaxTab        from '../../components/item/TaxTab';
import ClinicalTab   from '../../components/item/ClinicalTab';
import BarcodesTab   from '../../components/item/BarcodesTab';
import CostingTab    from '../../components/item/CostingTab';
import { createItem, updateItem, getItem, getCategories, getUoms, getUom } from '../../api/inventoryMaster';
import { getItemClasses } from '../../api/itemClass';
import { getMaterialGroups } from '../../api/materialGroup';
import { getTaxMasterByHsn } from '../../api/taxMaster';
import { getItemUomConversions } from '../../api/itemUom';
import useItemNames  from '../../hooks/useItemNames';
import {
  toSelectOptions, normalizeUom, normalizeUomList, normalizeCategoryList,
  normalizeItemClassList, normalizeMaterialGroupList,
  itemClassRowId, materialGroupRowId, uomRowId, inactiveMasterMessage,
} from '../../utils/masterEntity';
import {
  normalizeUomConversionList, buildConversionGraph, resolveConversion, resolveUomCodeById,
} from '../../utils/uomConversionEntity';
import { formatDate } from '../../utils';
import { cardSx }   from '../../theme';

const UomConversionTab = lazy(() => import('../../components/item/UomConversionTab'));

/** Tabs 0–2: guided wizard on new item. Tabs 3+ need a saved item id. */
const WIZARD_LAST_TAB = 2;

/** Identity required fields in UI order (serial focus). */
const IDENTITY_REQUIRED = [
  { name: 'material_group_id', label: 'Material Group' },
  { name: 'item_class_id', label: 'Item Class' },
  { name: 'category_id', label: 'Category' },
  { name: 'item_name', label: 'Item Name' },
  { name: 'base_uom_id', label: 'Base UOM' },
];
const IDENTITY_REQUIRED_NAMES = IDENTITY_REQUIRED.map((f) => f.name);
const LAST_REQUIRED_TOAST_ID = 'item-form-last-required';

const TAB_FIELDS = {
  0: IDENTITY_REQUIRED_NAMES,
  1: ['hsn_code'],
  2: ['return_behavior', 'shelf_life_days'],
};

/** Which tab owns a field. Single source of truth so tab routing and the tab
 *  error badge can't drift apart the way two hand-kept key lists would. */
function tabForField(field) {
  const entry = Object.entries(TAB_FIELDS).find(([, fields]) => fields.includes(field));
  return entry ? Number(entry[0]) : 0;
}

/** First errored field in tab order, so the user lands on the earliest problem. */
function firstErroredField(formErrors) {
  return Object.values(TAB_FIELDS).flat().find((name) => formErrors?.[name]);
}

function emptyIdentityRequired(values) {
  return IDENTITY_REQUIRED.filter((f) => !String(values?.[f.name] ?? '').trim());
}

/** RHF-managed fields. material_group_id / item_class_id are UI cascade only
 *  and are stripped from the create/update payload. */
const ITEM_FORM_FIELDS = {
  item_name: '', item_short_name: '',
  material_group_id: '', item_class_id: '', category_id: '', base_uom_id: '',
  default_purchase_uom_id: '', default_issue_uom_id: '',
  generic_name: '', brand_name: '', manufacturer_name: '',
  hsn_code: '', gst_rate: '',
  is_batch_tracked: false, is_expiry_tracked: false,
  is_controlled_drug: false, is_drug: false, is_narcotic: false, is_high_value: false,
  is_active: true,
  remarks: '',
  return_behavior: '', shelf_life_days: '',
};

const PAYLOAD_OMIT_KEYS = new Set([
  'item_code', 'item_type', 'material_group_id', 'item_class_id',
]);

const BOOL_FORM_KEYS = new Set([
  'is_batch_tracked', 'is_expiry_tracked', 'is_controlled_drug',
  'is_drug', 'is_narcotic', 'is_high_value', 'is_active',
]);

/** Mirror inventory-master rules before create/update. */
function validateDrugTrackingFlags(data) {
  const controlledOrNarcotic = Boolean(data.is_controlled_drug || data.is_narcotic);
  if (!controlledOrNarcotic) return null;
  if (!data.is_drug) {
    return 'Controlled/narcotic items must have is_drug=true';
  }
  if (!data.is_batch_tracked || !data.is_expiry_tracked) {
    return 'Controlled/narcotic items must have batch and expiry tracking enabled';
  }
  return null;
}

function resolveItemId(item) {
  return item?.item_id ?? item?.id ?? '';
}

/** Extract API/gateway error text from a thrown inventory client error. */
function resolveSaveErrorMessage(err) {
  return (
    err?.response?.data?.detail
    || err?.response?.data?.message
    || err?.apiResponse?.message
    || err?.message
    || ''
  );
}

/**
 * Map duplicate-item-code API messages to a stable UI copy.
 * e.g. "Item with item_code='CAL1000TAB001' already exists"
 *   → `Item code "CAL1000TAB001" already exists. Please use a different item code`
 */
function formatDuplicateItemCodeError(rawMessage, fallbackCode) {
  if (!rawMessage || !/already exists/i.test(rawMessage)) return null;
  if (!/item[_ ]?code/i.test(rawMessage)) return null;

  const match = /item[_ ]?code[=:\s]*['"]?([A-Za-z0-9_-]+)['"]?/i.exec(rawMessage);
  const code = match?.[1] || fallbackCode;
  if (!code) {
    return 'Item code already exists. Please use a different item code';
  }
  return `Item code "${code}" already exists. Please use a different item code`;
}

function categoryItemClassId(cat) {
  return cat?.item_class_id ?? cat?.item_class?.item_class_id ?? cat?.item_class?.id ?? '';
}

function itemClassGroupId(ic) {
  return ic?.material_group_id
    ?? ic?.material_group?.material_group_id
    ?? ic?.material_group?.id
    ?? '';
}

function isPharmacyMaterialGroup(group) {
  if (!group) return false;
  const tokens = [group.group_name, group.group_code, group.material_group_name]
    .map((v) => String(v || '').trim().toUpperCase())
    .filter(Boolean);
  return tokens.some((t) => t === 'PHARMACY' || t === 'PHARMA');
}

function resolveItemClassification(item, categoryList, classList) {
  const categoryId = item?.category_id ?? item?.category?.category_id ?? item?.category?.id ?? '';
  const cat = categoryList.find((c) => String(c.category_id) === String(categoryId));
  const itemClassId = item?.item_class_id
    ?? item?.item_class?.item_class_id
    ?? item?.item_class?.id
    ?? categoryItemClassId(cat);
  const ic = classList.find((row) => String(itemClassRowId(row)) === String(itemClassId));
  const materialGroupId = item?.material_group_id
    ?? item?.material_group?.material_group_id
    ?? item?.material_group?.id
    ?? cat?.material_group_id
    ?? cat?.material_group?.material_group_id
    ?? itemClassGroupId(ic);
  return { categoryId, itemClassId, materialGroupId };
}

function buildItemPayload(data) {
  const body = {};
  Object.keys(data).forEach((key) => {
    if (!PAYLOAD_OMIT_KEYS.has(key)) body[key] = data[key];
  });
  body.gst_rate = Number(data.gst_rate);
  // Optional UUID fields — the backend rejects '' for a uuid.UUID|None column.
  // Omit the key entirely when unset: update() uses exclude_unset=True, so this
  // leaves an untouched item's existing value alone instead of erroring on it.
  if (!body.default_purchase_uom_id) delete body.default_purchase_uom_id;
  if (!body.default_issue_uom_id) delete body.default_issue_uom_id;
  // Same rule for the two optional Clinical fields: the backend types them as
  // ReturnBehavior (defaults to REFUND) and int|None, and '' is neither — so a
  // blank field 422s unless the key is omitted and the backend default applies.
  if (!body.return_behavior) delete body.return_behavior;
  if (String(body.shelf_life_days ?? '').trim() === '') delete body.shelf_life_days;
  return body;
}

async function loadMasterLookup(label, fetcher, onFail) {
  try {
    return await fetcher();
  } catch (err) {
    console.error(`[ItemForm] ${label} lookup failed:`, err);
    onFail(label);
    return [];
  }
}

export default function ItemForm() {
  const history = useHistory();
  const location = useLocation();
  const { id }  = useParams();
  const { url } = useRouteMatch();
  const isEdit  = Boolean(id);
  const { refresh: refreshItemCatalog } = useItemNames();

  const [categories,      setCategories]      = useState([]);
  const [itemClasses,     setItemClasses]     = useState([]);
  const [materialGroups,  setMaterialGroups]  = useState([]);
  const [uoms,            setUoms]            = useState([]);
  const [saving,          setSaving]          = useState(false);
  const [initialLoading,  setInitialLoading]  = useState(isEdit);
  const [tabIndex,        setTabIndex]        = useState(0);
  const [maxUnlockedTab,  setMaxUnlockedTab]  = useState(isEdit ? 5 : 0);
  const [error,           setError]           = useState(null);
  const [successMessage,  setSuccessMessage]  = useState(null);
  const [lookupWarning,   setLookupWarning]   = useState(null);
  const [unresolvedUomCount, setUnresolvedUomCount] = useState(0);
  const [hsnRecord,       setHsnRecord]       = useState(null);
  const [hsnLookupIssue,  setHsnLookupIssue]  = useState(null);
  const [inactiveBaseUom, setInactiveBaseUom] = useState(null);
  const [versionConflict, setVersionConflict] = useState(false);
  // Phase 6: null = base_uom_id still editable; set = item already has stock
  // transactions and the backend will reject any change to it.
  const [baseUomLockedAt,      setBaseUomLockedAt]      = useState(null);
  const [saveBaseUomLockError, setSaveBaseUomLockError] = useState(null);
  const [focusIdentityPending, setFocusIdentityPending] = useState(false);

  /** Optimistic-locking token from the loaded item. Kept in a ref, not form state:
   *  it must never be user-editable and must survive setValue churn. */
  const loadedVersionRef = useRef(null);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    setFocus,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: ITEM_FORM_FIELDS,
  });

  const baseUomId = watch('base_uom_id');
  const itemName = watch('item_name');
  const categoryId = watch('category_id');
  const materialGroupId = watch('material_group_id');
  const itemClassId = watch('item_class_id');
  useEffect(() => {
    setValue('brand_name', itemName || '');
  }, [itemName, setValue]);

  // New items start fully consistent — purchase/issue UOM follow Base UOM until
  // the user deliberately picks something else. Only fills currently-blank
  // fields, so a manual override is never stomped by a later Base UOM change.
  // Edit mode never auto-fills: the value was already saved server-side.
  useEffect(() => {
    if (isEdit || !baseUomId) return;
    if (!getValues('default_purchase_uom_id')) setValue('default_purchase_uom_id', baseUomId);
    if (!getValues('default_issue_uom_id')) setValue('default_issue_uom_id', baseUomId);
  }, [baseUomId, isEdit, getValues, setValue]);

  // ── Load categories, UOMs, and item data ─────────────────────
  useEffect(() => {
    async function init() {
      const failedLookups = [];
      const onLookupFail = (label) => { failedLookups.push(label); };

      const [cats, uomList, groups, classes] = await Promise.all([
        loadMasterLookup('categories', () => getCategories({ is_active: true, limit: 200 }), onLookupFail),
        loadMasterLookup('UOMs', () => getUoms({ is_active: true, limit: 200 }), onLookupFail),
        loadMasterLookup('material groups', () => getMaterialGroups({ is_active: true, limit: 200 }), onLookupFail),
        loadMasterLookup('item classes', () => getItemClasses({ is_active: true, limit: 200 }), onLookupFail),
      ]);

      if (failedLookups.length) {
        setLookupWarning(
          `Could not load ${failedLookups.join(', ')}. Some dropdowns may be empty — please refresh the page.`,
        );
      } else {
        setLookupWarning(null);
      }

      setCategories(normalizeCategoryList(cats));
      setUoms(normalizeUomList(uomList));
      setMaterialGroups(normalizeMaterialGroupList(groups));
      setItemClasses(normalizeItemClassList(classes));

      if (isEdit) {
        try {
          const item = await getItem(id);
          loadedVersionRef.current = item.version ?? null;
          setBaseUomLockedAt(item.first_transaction_posted_at || null);
          Object.keys(ITEM_FORM_FIELDS).forEach(k => {
            if (!(k in item)) return;
            setValue(k, BOOL_FORM_KEYS.has(k) ? Boolean(item[k]) : item[k]);
          });
          const classification = resolveItemClassification(
            item,
            normalizeCategoryList(cats),
            normalizeItemClassList(classes),
          );
          setValue('category_id', classification.categoryId);
          setValue('item_class_id', classification.itemClassId || '');
          setValue('material_group_id', classification.materialGroupId || '');
          setValue(
            'base_uom_id',
            item.base_uom_id ?? item.base_uom?.uom_id ?? item.base_uom?.id ?? '',
          );
          const savedUomId = item.base_uom_id ?? item.base_uom?.uom_id ?? item.base_uom?.id ?? '';
          const activeUoms = normalizeUomList(uomList);
          const savedInActiveList = activeUoms.some(u => String(uomRowId(u)) === String(savedUomId));
          if (savedUomId && !savedInActiveList) {
            const nested = item.base_uom;
            if (nested && (nested.uom_id || nested.id)) {
              setInactiveBaseUom(normalizeUom({ ...nested, is_active: false }));
            } else {
              try {
                const fetched = await getUom(savedUomId);
                setInactiveBaseUom(normalizeUom({ ...fetched, is_active: false }));
              } catch {
                setInactiveBaseUom({ uom_id: savedUomId, uom_name: 'UOM', is_active: false });
              }
            }
          } else {
            setInactiveBaseUom(null);
          }
          if (item.hsn_code) {
            const fallbackRecord = { hsn_code: item.hsn_code, gst_rate: item.gst_rate };
            try {
              const taxRecord = await getTaxMasterByHsn(item.hsn_code);
              if (taxRecord) {
                setHsnRecord(taxRecord);
                setHsnLookupIssue(taxRecord.is_active === false ? 'inactive' : null);
              } else {
                setHsnRecord(fallbackRecord);
                setHsnLookupIssue('unavailable');
              }
            } catch {
              setHsnRecord(fallbackRecord);
              setHsnLookupIssue('unavailable');
            }
          }
        } catch (err) {
          console.error('[ItemForm] failed to load item for edit:', err);
          setError('Failed to load item data. Please go back and try again.');
        }
        setInitialLoading(false);
      }
    }
    init();
  }, [id, isEdit, setValue]);

  // ── Post-create / post-save navigation state ─────────────────
  useEffect(() => {
    if (!location.state?.saved) return;

    if (location.state.created) {
      setSuccessMessage(
        'Item created successfully. Continue with UOM conversions, barcodes, and costing below.',
      );
    } else {
      setSuccessMessage('Item saved successfully.');
    }

    if (typeof location.state.nextTab === 'number') {
      setTabIndex(location.state.nextTab);
    }

    history.replace(location.pathname, {});
  }, [location.state, location.pathname, history]);

  const focusFirstEmptyIdentity = useCallback((focusedName) => {
    const empty = emptyIdentityRequired(getValues());
    if (!empty.length) return;

    const first = empty[0];
    if (focusedName === first.name) return;

    const focusedIdx = IDENTITY_REQUIRED.findIndex((f) => f.name === focusedName);
    const firstIdx = IDENTITY_REQUIRED.findIndex((f) => f.name === first.name);
    if (focusedIdx !== -1 && focusedIdx <= firstIdx) return;

    setFocus(first.name);
  }, [getValues, setFocus]);

  // Jump to + focus Base UOM once the Identity tab has actually mounted it —
  // setFocus right after setTabIndex would miss the ref, since inactive tab
  // panels aren't mounted (see ItemFormTabs' TabPanel).
  useEffect(() => {
    if (saveBaseUomLockError && tabIndex === 0) {
      setFocus('base_uom_id');
    }
  }, [saveBaseUomLockError, tabIndex, setFocus]);

  // Same deferral for a failed submit raised from another tab: the Identity
  // panel has to mount before its first empty field can take focus.
  useEffect(() => {
    if (!focusIdentityPending || tabIndex !== 0) return;
    focusFirstEmptyIdentity();
    setFocusIdentityPending(false);
  }, [focusIdentityPending, tabIndex, focusFirstEmptyIdentity]);

  // The error Alert sits at the top of the form, which is off-screen once the
  // user has scrolled down to the action bar — so every failure also goes out
  // as a toast, which is position-fixed and visible wherever they are.
  useEffect(() => {
    if (error) toast.error(error, { toastId: 'item-form-error' });
  }, [error]);

  useEffect(() => {
    if (initialLoading) return;
    const values = {
      material_group_id: materialGroupId,
      item_class_id: itemClassId,
      item_name: itemName,
      category_id: categoryId,
      base_uom_id: baseUomId,
    };
    const empty = emptyIdentityRequired(values);
    if (empty.length !== 1) {
      toast.dismiss(LAST_REQUIRED_TOAST_ID);
      return;
    }
    toast.info(`Please fill ${empty[0].label}`, { toastId: LAST_REQUIRED_TOAST_ID });
  }, [materialGroupId, itemClassId, itemName, categoryId, baseUomId, initialLoading]);

  async function goNext() {
    const fields = TAB_FIELDS[tabIndex] ?? [];
    const valid = fields.length ? await trigger(fields) : true;
    if (!valid) {
      if (tabIndex === 0) focusFirstEmptyIdentity();
      return;
    }

    const next = Math.min(tabIndex + 1, WIZARD_LAST_TAB);
    setMaxUnlockedTab((prev) => Math.max(prev, next));
    setTabIndex(next);
  }

  function goBack() {
    setTabIndex((prev) => Math.max(prev - 1, 0));
  }

  function handleTabChange(newIndex) {
    if (!isEdit) {
      if (newIndex > maxUnlockedTab) return;
    }
    setTabIndex(newIndex);
  }

  /** STRICT gate (edit only): a chosen purchase/issue UOM must resolve to Base
   *  UOM via the item's conversion rules, otherwise GRN/issue posting will
   *  422 on it later — catch it here instead. Fails open on a lookup error;
   *  an unrelated field edit must never be blocked by a rules-fetch hiccup. */
  async function unresolvedPurchaseIssueMessage(data) {
    const checks = [
      ['Purchase UOM', data.default_purchase_uom_id],
      ['Issue UOM', data.default_issue_uom_id],
    ].filter(([, uomId]) => uomId && String(uomId) !== String(data.base_uom_id));
    if (!checks.length) return null;

    try {
      const raw = await getItemUomConversions(id);
      const conversions = normalizeUomConversionList(raw, uomOptions);
      const graph = buildConversionGraph(conversions);
      for (const [label, uomId] of checks) {
        const code = resolveUomCodeById(uomId, uomOptions);
        if (!code || resolveConversion(graph, code, baseUomName) == null) {
          return `${label} has no conversion rule connecting it to the Base UOM yet. Add one on the UOM Conversion tab first.`;
        }
      }
    } catch {
      return null;
    }
    return null;
  }

  // ── Submit ───────────────────────────────────────────────────
  async function onSubmit(data) {
    setVersionConflict(false);

    const clinicalError = validateDrugTrackingFlags(data);
    if (clinicalError) {
      setError(clinicalError);
      setTabIndex(2);
      return;
    }

    const inactiveUomId = inactiveBaseUom ? uomRowId(inactiveBaseUom) : '';
    if (inactiveUomId && String(data.base_uom_id) === String(inactiveUomId)) {
      setError(inactiveMasterMessage('This UOM'));
      setTabIndex(0);
      return;
    }

    if (isEdit) {
      const unresolvedMsg = await unresolvedPurchaseIssueMessage(data);
      if (unresolvedMsg) {
        setError(unresolvedMsg);
        setTabIndex(3);
        return;
      }
    }

    setSaving(true);
    setError(null);
    setSaveBaseUomLockError(null);
    try {
      const body = buildItemPayload(data);
      if (isEdit) {
        // Optimistic locking — backend returns 409 when someone else saved first.
        if (loadedVersionRef.current != null) body.version = loadedVersionRef.current;
        const updated = await updateItem(id, body);
        if (updated?.version != null) loadedVersionRef.current = updated.version;
        await refreshItemCatalog();
        const listUrl = url.replace(`/${id}/edit`, '');
        history.push(listUrl, { saved: true });
      } else {
        const created = await createItem(body);
        await refreshItemCatalog();
        const newId = resolveItemId(created);
        const listUrl = url.replace('/new', '');
        if (newId) {
          history.push(`${listUrl}/${newId}/edit`, {
            saved: true,
            created: true,
            nextTab: 3,
          });
        } else {
          history.push(listUrl, { saved: true });
        }
      }
    } catch (err) {
      console.error('[ItemForm] save failed:', err);
      const rawMessage = resolveSaveErrorMessage(err);

      // Duplicate item code (often HTTP 409) — never treat as version conflict.
      const duplicateMessage = formatDuplicateItemCodeError(rawMessage);
      if (duplicateMessage) {
        setVersionConflict(false);
        setError(duplicateMessage);
        setTabIndex(0);
        return;
      }

      // Base UOM locked (Phase 6) — item already has stock transactions. The
      // Identity tab pre-emptively disables the field once we know this, but
      // this stays as the authoritative fallback for a race (e.g. a
      // transaction posted on this item after the page loaded).
      const lockMatch = /base_uom_id is locked[^(]*\(first posted ([^)]+)\)/i.exec(rawMessage);
      if (lockMatch) {
        const friendly = `This item's Base UOM can't be changed — it already has stock `
          + `transactions since ${formatDate(lockMatch[1])}.`;
        setVersionConflict(false);
        setError(friendly);
        setSaveBaseUomLockError(friendly);
        setBaseUomLockedAt(lockMatch[1]);
        setTabIndex(0);
        return;
      }

      // Optimistic locking / version conflict only (message-based, not all 409s).
      if (/version conflict/i.test(rawMessage)) {
        setVersionConflict(true);
        setError(
          'This item was changed by someone else while you were editing. '
          + 'Reload the page to see the latest version, then re-apply your changes.',
        );
        return;
      }

      // Other concurrent-update 409s — show API text, not the version-conflict copy.
      if (err?.status === 409) {
        setVersionConflict(false);
        setError(
          rawMessage
          || 'This item could not be saved because of a concurrent update. Please try again.',
        );
        return;
      }

      const message = rawMessage || 'Something went wrong. Please try again.';
      setVersionConflict(false);
      setError(message);
      // Category → item class link may have changed server-side (deleted/re-parented) — refresh the dropdown.
      if (/category.*(not linked|item class)/i.test(message)) {
        loadMasterLookup('categories', () => getCategories({ is_active: true, limit: 200 }), () => {})
          .then(cats => setCategories(normalizeCategoryList(cats)));
      }
    } finally {
      setSaving(false);
    }
  }

  const materialGroupOptions = useMemo(() => toSelectOptions(materialGroups, {
    idKey: 'material_group_id', nameKey: 'group_name', codeKey: 'group_code',
  }), [materialGroups]);

  const filteredItemClasses = useMemo(() => {
    if (!materialGroupId) return [];
    const matched = itemClasses.filter(
      (ic) => String(itemClassGroupId(ic)) === String(materialGroupId),
    );
    if (itemClassId && !matched.some((ic) => String(itemClassRowId(ic)) === String(itemClassId))) {
      const current = itemClasses.find((ic) => String(itemClassRowId(ic)) === String(itemClassId));
      if (current) return [...matched, current];
    }
    return matched;
  }, [itemClasses, materialGroupId, itemClassId]);

  const classOptions = useMemo(() => toSelectOptions(filteredItemClasses, {
    idKey: 'item_class_id', nameKey: 'class_name', codeKey: 'class_code',
  }), [filteredItemClasses]);

  const filteredCategories = useMemo(() => {
    if (!itemClassId) return [];
    const matched = categories.filter(
      (c) => String(categoryItemClassId(c)) === String(itemClassId),
    );
    if (categoryId && !matched.some((c) => String(c.category_id) === String(categoryId))) {
      const current = categories.find((c) => String(c.category_id) === String(categoryId));
      if (current) return [...matched, current];
    }
    return matched;
  }, [categories, itemClassId, categoryId]);

  const catOptions = useMemo(() => toSelectOptions(filteredCategories, {
    idKey: 'category_id', nameKey: 'category_name', codeKey: 'category_code',
  }), [filteredCategories]);
  const uomOptions = useMemo(() => toSelectOptions(uoms, {
    idKey: 'uom_id', nameKey: 'uom_name', codeKey: 'uom_code',
  }), [uoms]);

  const uomIsInactive = Boolean(
    inactiveBaseUom && String(baseUomId) === String(uomRowId(inactiveBaseUom)),
  );

  // A failed save takes precedence (it's the freshest signal, e.g. a race
  // where a transaction posted after this page loaded); otherwise fall back
  // to what the item told us on load.
  const baseUomLockMessage = saveBaseUomLockError || (
    isEdit && baseUomLockedAt
      ? `Locked — this item already has stock transactions since ${formatDate(baseUomLockedAt)}. `
        + 'Base UOM can no longer be changed.'
      : ''
  );

  const identityUomOptions = useMemo(() => {
    if (!uomIsInactive) return uomOptions;
    const id = uomRowId(inactiveBaseUom);
    if (uomOptions.some(o => String(o.value) === String(id))) return uomOptions;
    const name = inactiveBaseUom.uom_name || 'UOM';
    const code = inactiveBaseUom.uom_code;
    const label = `${code ? `${name} (${code})` : name} — Inactive`;
    return [{ value: id, label }, ...uomOptions];
  }, [uomOptions, uomIsInactive, inactiveBaseUom]);

  const selectedCategory = useMemo(
    () => categories.find(c => String(c.category_id) === String(categoryId)),
    [categories, categoryId],
  );

  // Category is_drug_category is a UI hint only — soft-suggest item is_drug (never clears it).
  useEffect(() => {
    if (!selectedCategory?.is_drug_category) return;
    setValue('is_drug', true, { shouldDirty: true });
  }, [selectedCategory?.is_drug_category, selectedCategory?.category_id, setValue]);

  // Prefer select options (value/label); fall back to raw uom_code from master list
  const baseUomName = useMemo(() => {
    if (!baseUomId) return '';
    if (uomIsInactive) return inactiveBaseUom?.uom_code || '';
    const opt = uomOptions.find((u) => String(u.value) === String(baseUomId));
    if (opt?.label) {
      const match = opt.label.match(/\(([^)]+)\)$/);
      return match ? match[1] : opt.label;
    }
    const raw = uoms.find((u) => String(u.uom_id ?? u.id) === String(baseUomId));
    return raw?.uom_code || '';
  }, [baseUomId, uomOptions, uoms, uomIsInactive, inactiveBaseUom]);

  function tabStatus(i) {
    if ((TAB_FIELDS[i] ?? []).some(k => errors[k])) return 'error';
    if (!isEdit && i <= WIZARD_LAST_TAB && i < maxUnlockedTab) return 'complete';
    return 'empty';
  }

  function onInvalidSubmit(formErrors) {
    const field = firstErroredField(formErrors);
    if (!field) return;
    const tab = tabForField(field);
    setTabIndex(tab);
    // Only the Identity fields wire RHF's ref (inputRef={field.ref}), so that's
    // the one tab where focus can actually land. Elsewhere the red tab badge and
    // the toast carry the message instead of a focus call that would no-op.
    if (tab === 0) setFocusIdentityPending(true);
  }
  const completionStatus = Array.from({ length: 6 }, (_, i) => tabStatus(i));
  const showWizard = !isEdit;
  const onLastWizardStep = showWizard && tabIndex === WIZARD_LAST_TAB;

  // ── Shared action buttons ────────────────────────────────────
  const formActions = (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      flexWrap: 'wrap',
      gap: 1.5,
    }}>
      <InvButton variant="outlined" tone="teal" onClick={() => history.goBack()}>
        Cancel
      </InvButton>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        {showWizard && (
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mr: 0.5 }}>
            Step {tabIndex + 1} of {WIZARD_LAST_TAB + 1}
          </Typography>
        )}
        {showWizard && tabIndex > 0 && (
          <InvButton variant="outlined" tone="teal" type="button" onClick={goBack}>
            Back
          </InvButton>
        )}
        {showWizard && !onLastWizardStep && (
          <InvButton type="button" onClick={goNext}>
            Next
          </InvButton>
        )}
        {(isEdit || onLastWizardStep) && (
          <InvButton type="submit" loading={saving}>
            {isEdit ? 'Save Changes' : 'Create Item'}
          </InvButton>
        )}
      </Box>
    </Box>
  );

  // ── Loading skeleton ─────────────────────────────────────────
  if (initialLoading) {
    return (
      <Box>
        <Box sx={{ mb: 2.5 }}>
          <Skeleton variant="text" width={200} height={36} />
        </Box>
        <Box sx={{ ...cardSx, borderRadius: '12px', overflow: 'hidden' }}>
          <Skeleton variant="rectangular" height={44} sx={{ borderRadius: 0 }} />
          <Box sx={{ p: 2.5 }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={8}>
                <Skeleton variant="rectangular" height={160} sx={{ borderRadius: '10px', mb: 2.5 }} />
                <Skeleton variant="rectangular" height={100} sx={{ borderRadius: '10px' }} />
              </Grid>
            </Grid>
          </Box>
          <Box sx={{ borderTop: '1px solid #e9ecef', px: 2.5, py: 2, display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
            <Skeleton variant="rectangular" width={90}  height={36} sx={{ borderRadius: '10px' }} />
            <Skeleton variant="rectangular" width={120} height={36} sx={{ borderRadius: '10px' }} />
          </Box>
        </Box>
      </Box>
    );
  }

  // ── Render ───────────────────────────────────────────────────
  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit, onInvalidSubmit)}>
      <PageHeader
        eyebrow="Material Management"
        title={isEdit ? 'Edit Item' : 'New Item'}
      />

      {lookupWarning && (
        <Alert severity="warning" onClose={() => setLookupWarning(null)} sx={{ mb: 2 }}>
          {lookupWarning}
        </Alert>
      )}

      {isEdit && unresolvedUomCount > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {unresolvedUomCount} UOM{unresolvedUomCount > 1 ? 's' : ''} on this item{' '}
          {unresolvedUomCount > 1 ? 'are' : 'is'} not yet resolvable to the base UOM. Open the
          UOM Conversion tab to add the missing rule(s).
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {!isEdit && tabIndex <= WIZARD_LAST_TAB && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Complete Identity, Taxation, and Clinical details, then create the item to unlock
          UOM conversions, barcodes, and costing.
        </Alert>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          {...(versionConflict
            ? {
              action: (
                <InvButton
                  size="small"
                  variant="outlined"
                  tone="teal"
                  onClick={() => window.location.reload()}
                >
                  Reload
                </InvButton>
              ),
            }
            : {
              onClose: () => {
                setError(null);
              },
            })}
        >
          {error}
        </Alert>
      )}

      <ItemFormTabs
        tabIndex={tabIndex}
        onTabChange={handleTabChange}
        isEdit={isEdit}
        maxUnlockedTab={maxUnlockedTab}
        completionStatus={completionStatus}
        actions={formActions}
      >
        {/* Tab 0 — Identity */}
        <IdentityTab
          control={control}
          errors={errors}
          setValue={setValue}
          onGuardFocus={focusFirstEmptyIdentity}
          materialGroups={materialGroupOptions}
          itemClasses={classOptions}
          categories={catOptions}
          uoms={identityUomOptions}
          uomHelperText={
            baseUomLockMessage || (uomIsInactive ? inactiveMasterMessage('This UOM') : undefined)
          }
          baseUomDisabled={Boolean(isEdit && baseUomLockedAt)}
          baseUomLockError={Boolean(baseUomLockMessage)}
          classDisabled={!materialGroupId}
          categoryDisabled={!itemClassId}
          classHelperText={
            !materialGroupId
              ? 'Select a material group first'
              : (classOptions.length === 0 ? 'No item classes for this group' : undefined)
          }
          categoryHelperText={
            !itemClassId
              ? 'Select an item class first'
              : (catOptions.length === 0 ? 'No categories for this class' : undefined)
          }
          isPharmacy={isPharmacyMaterialGroup(
            materialGroups.find((g) => String(materialGroupRowId(g)) === String(materialGroupId)),
          )}
        />

        {/* Tab 1 — Taxation */}
        <TaxTab
          control={control}
          errors={errors}
          setValue={setValue}
          hsnValueRecord={hsnRecord}
          hsnWarning={hsnLookupIssue}
        />

        {/* Tab 2 — Clinical */}
        <ClinicalTab control={control} setValue={setValue} watch={watch} />

        {/* Tab 3 — UOM Conversion (lazy-loaded) */}
        <Suspense fallback={(
          <Box sx={{ py: 2 }}>
            <Skeleton variant="rounded" height={48} sx={{ mb: 2, borderRadius: 2 }} />
            <Skeleton variant="rounded" height={280} sx={{ borderRadius: 2 }} />
          </Box>
        )}
        >
          <UomConversionTab
            itemId={isEdit ? id : null}
            baseUomId={baseUomId}
            baseUomName={baseUomName}
            uoms={uomOptions}
            onUnresolvedChange={setUnresolvedUomCount}
          />
        </Suspense>

        {/* Tab 4 — Barcodes (requires saved item) */}
        <BarcodesTab itemId={isEdit ? id : null} uoms={uomOptions} />

        {/* Tab 5 — Costing (requires saved item) */}
        <CostingTab itemId={isEdit ? id : null} />
      </ItemFormTabs>
    </Box>
  );
}
